import { onReady } from './dom.js';

// The filter panel is only a drawer on small screens.
//
// Flowbite drives it from the `data-drawer-*` attributes in filtersApplied.twig
// and sets `aria-hidden="true"` on #filter-drawer whenever the drawer is closed.
// That is right on mobile, where the panel really is off-canvas. On desktop the
// `sm:` utilities render it inline and fully visible, and Flowbite is never asked
// to open it -- so the attribute stays on a panel everybody can see, and
// `aria-hidden` on an ancestor removes the whole subtree from the accessibility
// tree. Screen-reader users got no filters at all: no categories, no areas, no
// dates, no accessibility filters. See #293.
//
// 640px is Tailwind's `sm` breakpoint, where those utilities take over.
var DESKTOP = '(min-width: 640px)';

onReady(function () {
    var drawer = document.getElementById('filter-drawer');

    if (!drawer) {
        return;
    }

    var desktop = window.matchMedia(DESKTOP);

    // Both branches must be no-ops when the attribute is already right: setAttribute
    // emits a mutation record even when the value is unchanged, so writing
    // unconditionally makes the observer below retrigger this function forever and the
    // page never finishes loading.
    function sync() {
        if (desktop.matches) {
            if (drawer.hasAttribute('aria-hidden')) {
                drawer.removeAttribute('aria-hidden');
            }
        } else if (
            drawer.classList.contains('-translate-x-full')
            && drawer.getAttribute('aria-hidden') !== 'true'
        ) {
            // Back to mobile with the drawer closed: it is off-canvas again.
            drawer.setAttribute('aria-hidden', 'true');
        }

        syncExpanded();
    }

    // Flowbite's data-attribute drawers set aria-hidden on the panel but never touch
    // aria-expanded on the button that opens it, so the toggle announced itself as a
    // plain button and gave no way to tell whether the filters were open or closed —
    // which is the one thing it exists to say (WCAG 4.1.2, #363).
    //
    // Derived from the panel's own position rather than tracked separately: the drawer
    // can be closed by its x, by Escape below, or by Flowbite's backdrop, and a counter
    // kept alongside those would drift out of step with at least one of them.
    function syncExpanded() {
        var toggle = document.querySelector('[data-drawer-show="filter-drawer"]');
        if (!toggle) {
            return;
        }

        // On desktop the panel is inline furniture and the toggle is display:none, so
        // "expanded" is the honest answer for the state it would be in if shown.
        var open = desktop.matches || !drawer.classList.contains('-translate-x-full');
        var next = open ? 'true' : 'false';

        if (toggle.getAttribute('aria-expanded') !== next) {
            toggle.setAttribute('aria-expanded', next);
        }
    }

    // Flowbite initialises on its own DOMContentLoaded handler and re-applies
    // aria-hidden on every hide, so watching the attribute is more reliable than
    // racing it.
    // `class` as well as `aria-hidden`: Flowbite opens the drawer by removing
    // -translate-x-full and only sets aria-hidden on the way closed, so watching the
    // attribute alone never sees an open. sync() writes to the toggle and to
    // aria-hidden, never to the drawer's class, so this cannot feed itself.
    new MutationObserver(sync).observe(drawer, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'class'],
    });

    desktop.addEventListener('change', sync);
    sync();

    // Escape closes the drawer.
    //
    // Flowbite's data-attribute drawers bind a toggle and a close button but no key
    // handler, so on a phone the only way out was to find the small x in the corner.
    // Escape is what anyone who has met a dialog will try first, and WCAG 2.1.1 expects
    // every control to be operable from the keyboard rather than by pointing at it.
    //
    // Only on mobile: on desktop the panel is inline furniture, not a dialog, and
    // hiding it on a stray Escape would take the filters away from everyone.
    document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape' || desktop.matches) {
            return;
        }
        if (drawer.classList.contains('-translate-x-full')) {
            return; // Already closed.
        }

        var close = drawer.querySelector('[data-drawer-hide="filter-drawer"]');
        if (close) {
            close.click();
            // Send focus back to the control that opened it, or it is left on <body>
            // and the next Tab starts again from the top of the page.
            var opener = document.querySelector('[data-drawer-show="filter-drawer"]');
            if (opener) {
                opener.focus();
            }
        }
    });
});
